import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const SUPPORTED_LANGS = ["fr", "en"] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];
export const DEFAULT_LANG: SupportedLang = "fr";

/**
 * Extracts language code from the pathname if present ('/en/...' -> 'en', '/fr/...' -> 'fr')
 */
export function getLangFromPathname(pathname: string): SupportedLang {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0]?.toLowerCase();
  if (first === "en" || first === "fr") {
    return first as SupportedLang;
  }
  return DEFAULT_LANG;
}

/**
 * Returns a localized path preserving the rest of the route
 * e.g. getLocalizedPath('/programs', 'en') -> '/en/programs'
 * e.g. getLocalizedPath('/en/programs', 'fr') -> '/programs' (or '/fr/programs')
 */
export function getLocalizedPath(pathname: string, targetLang: SupportedLang): string {
  const segments = pathname.split("/").filter(Boolean);
  const first = segments[0]?.toLowerCase();
  
  const hasLangPrefix = first === "en" || first === "fr";
  const cleanSegments = hasLangPrefix ? segments.slice(1) : segments;
  const cleanPath = "/" + cleanSegments.join("/");

  if (targetLang === DEFAULT_LANG) {
    return cleanPath === "" ? "/" : cleanPath;
  }
  return `/${targetLang}${cleanPath === "/" ? "" : cleanPath}`;
}

export default function LanguageRouteSync() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const lang = getLangFromPathname(location.pathname);
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    document.documentElement.lang = lang;
  }, [location.pathname, i18n]);

  return null;
}
